import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Loader2, FileText, CheckCircle, Mail, AlertCircle, RefreshCw } from 'lucide-react';
import NavBar from '../components/NavBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GenerateReportPage = () => {
  const { assessmentId } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('initializing'); // initializing, generating, completed, failed
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const pollingInterval = useRef(null);
  
  const progressMessages = {
    tr: [
      'Değerlendirme verileri analiz ediliyor...',
      'AI motoru başlatılıyor...',
      'Pillar skorları hesaplanıyor...',
      'Benchmark karşılaştırması yapılıyor...',
      'Yönetici özeti oluşturuluyor...',
      'Boşluk analizi hazırlanıyor...',
      'AI hazırlık analizi yapılıyor...',
      'Risk değerlendirmesi yapılıyor...',
      'Dönüşüm yol haritası oluşturuluyor...',
      'ROI simülasyonu hesaplanıyor...',
      'Rapor son haline getiriliyor...'
    ],
    en: [
      'Analyzing assessment data...',
      'Initializing AI engine...',
      'Calculating pillar scores...',
      'Performing benchmark comparison...',
      'Generating executive summary...',
      'Preparing gap analysis...',
      'Conducting AI readiness analysis...',
      'Performing risk assessment...',
      'Creating transformation roadmap...',
      'Calculating ROI simulation...',
      'Finalizing report...'
    ]
  };
  
  useEffect(() => {
    startReportGeneration();
    
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [assessmentId]);
  
  useEffect(() => {
    // Update progress message based on progress value
    if (status === 'generating') {
      const messages = progressMessages[language] || progressMessages.en;
      const messageIndex = Math.min(Math.floor(progress / 10), messages.length - 1);
      setStatusMessage(messages[messageIndex]);
    }
  }, [progress, status, language]);
  
  const startReportGeneration = async () => {
    try {
      setStatus('generating');
      setProgress(5);
      
      const response = await axios.post(`${API}/reports/generate`, {
        assessment_id: assessmentId
      });
      
      if (response.data.status === 'completed' && response.data.report) {
        // Report already exists
        setReport(response.data.report);
        setStatus('completed');
        setProgress(100);
      } else if (response.data.status === 'started' || response.data.status === 'generating') {
        // Start polling for status
        startPolling();
      } else {
        setError(language === 'tr' ? 'Beklenmeyen durum' : 'Unexpected status');
        setStatus('failed');
      }
    } catch (err) {
      console.error('Error starting report generation:', err);
      setError(err.response?.data?.detail || (language === 'tr' ? 'Rapor oluşturulamadı' : 'Error generating report'));
      setStatus('failed');
    }
  };
  
  const startPolling = () => {
    // Clear any existing interval
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }
    
    // Start simulated progress while polling
    let currentProgress = 10;
    const progressTimer = setInterval(() => {
      currentProgress = Math.min(currentProgress + Math.random() * 5, 90);
      setProgress(currentProgress);
    }, 3000);
    
    // Poll every 5 seconds
    pollingInterval.current = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/reports/status/${assessmentId}`);
        
        if (response.data.status === 'completed') {
          clearInterval(pollingInterval.current);
          clearInterval(progressTimer);
          setProgress(100);
          setReport(response.data.report);
          setStatus('completed');
        } else if (response.data.status === 'failed') {
          clearInterval(pollingInterval.current);
          clearInterval(progressTimer);
          setError(response.data.error || (language === 'tr' ? 'Rapor oluşturulamadı' : 'Report generation failed'));
          setStatus('failed');
        }
        // Continue polling if still generating
      } catch (err) {
        console.error('Error polling report status:', err);
        // Don't stop polling on error, might be temporary
      }
    }, 5000);
    
    // Stop polling after 5 minutes (timeout)
    setTimeout(() => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        clearInterval(progressTimer);
        if (status === 'generating') {
          setStatus('failed');
          setError(language === 'tr' 
            ? 'Rapor oluşturma zaman aşımına uğradı. Raporunuz arka planda oluşturulmaya devam ediyor. Lütfen birkaç dakika sonra raporlar sayfasını kontrol edin.'
            : 'Report generation timed out. Your report is still being generated in the background. Please check the reports page in a few minutes.');
        }
      }
    }, 5 * 60 * 1000);
  };
  
  const handleRetry = () => {
    setError(null);
    setStatus('initializing');
    setProgress(0);
    startReportGeneration();
  };
  
  // Generating view
  if (status === 'generating' || status === 'initializing') {
    return (
      <div className="min-h-screen bg-slate-950" data-testid="report-generating">
        <NavBar />
        <div className="pt-24 flex items-center justify-center px-4">
          <Card className="bg-slate-900/50 border-slate-800 w-full max-w-lg">
            <CardContent className="py-12 text-center">
              <div className="relative w-24 h-24 mx-auto mb-8">
                <div className="absolute inset-0 rounded-full border-4 border-slate-700"></div>
                <div 
                  className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"
                  style={{ animationDuration: '1.5s' }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-blue-400">{Math.round(progress)}%</span>
                </div>
              </div>
              
              <h2 className="text-xl font-semibold text-white mb-3">
                {language === 'tr' ? 'AI Raporu Oluşturuluyor' : 'Generating AI Report'}
              </h2>
              
              <p className="text-slate-400 mb-6 min-h-[24px]">
                {statusMessage}
              </p>
              
              <Progress value={progress} className="h-2 bg-slate-800 mb-4" />
              
              <div className="mt-8 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-left">
                    <p className="text-sm text-blue-300 font-medium">
                      {language === 'tr' ? 'Sayfadan ayrılabilirsiniz' : 'You can leave this page'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {language === 'tr' 
                        ? 'Raporunuz hazır olduğunda raporlar sayfasından erişebilirsiniz.'
                        : 'When your report is ready, you can access it from the reports page.'}
                    </p>
                  </div>
                </div>
              </div>
              
              <Button
                variant="outline"
                className="mt-6 border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() => navigate('/reports')}
              >
                {language === 'tr' ? 'Raporlar Sayfasına Git' : 'Go to Reports'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // Error view
  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-slate-950" data-testid="report-failed">
        <NavBar />
        <div className="pt-24 flex items-center justify-center px-4">
          <Card className="bg-slate-900/50 border-slate-800 w-full max-w-lg">
            <CardContent className="py-12 text-center">
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="h-10 w-10 text-red-400" />
              </div>
              
              <h2 className="text-xl font-semibold text-white mb-3">
                {language === 'tr' ? 'Rapor Oluşturulamadı' : 'Report Generation Failed'}
              </h2>
              
              <p className="text-slate-400 mb-6">{error}</p>
              
              <div className="flex justify-center space-x-4">
                <Button 
                  onClick={handleRetry}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="retry-btn"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {language === 'tr' ? 'Tekrar Dene' : 'Retry'}
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={() => navigate('/reports')}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                >
                  {language === 'tr' ? 'Raporlara Git' : 'Go to Reports'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }
  
  // Completed view
  return (
    <div className="min-h-screen bg-slate-950" data-testid="report-completed">
      <NavBar />
      <div className="pt-24 flex items-center justify-center px-4">
        <Card className="bg-slate-900/50 border-slate-800 w-full max-w-lg">
          <CardContent className="py-12 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-emerald-400" />
            </div>
            
            <h2 className="text-xl font-semibold text-white mb-3">
              {language === 'tr' ? 'Rapor Hazır!' : 'Report Ready!'}
            </h2>
            
            <p className="text-slate-400 mb-8">
              {language === 'tr' 
                ? 'AI destekli dijital olgunluk raporunuz başarıyla oluşturuldu.'
                : 'Your AI-powered digital maturity report has been successfully generated.'}
            </p>
            
            {report && (
              <div className="mb-8 p-4 rounded-lg bg-slate-800/50 text-left">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-400">GDMII Score</span>
                  <span className="text-2xl font-bold text-transparent bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text">
                    {report.gdmii_score?.toFixed(1)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-400">
                    {language === 'tr' ? 'Benchmark Pozisyonu' : 'Benchmark Position'}
                  </span>
                  <span className="text-sm text-blue-400 font-medium">
                    {report.benchmark_position?.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              </div>
            )}
            
            <Button 
              onClick={() => navigate(`/reports/${report?.id}`)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 w-full"
              data-testid="view-report-btn"
            >
              <FileText className="mr-2 h-4 w-4" />
              {language === 'tr' ? 'Raporu Görüntüle' : 'View Report'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GenerateReportPage;
